import React, { useState, useEffect } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { MdMenu, MdClose, MdDashboard, MdConstruction } from "react-icons/md";
import { BsGrid, BsBuilding, BsBoxArrowRight, BsChatDots, BsCreditCard } from "react-icons/bs";
import { AiOutlinePlusSquare } from "react-icons/ai";

const NAV_ITEMS = [
  { label: "Add Drawing", to: "/dashboard", icon: <AiOutlinePlusSquare size={18} /> },
  { label: "All Drawings", to: "/all-drawings", icon: <BsGrid size={16} /> },
  { label: "Add Construction", to: "/add-construction", icon: <MdConstruction size={18} /> },
  { label: "All Constructs", to: "/all-constructs", icon: <BsBuilding size={16} /> },
  { label: "Live Chats", to: "/admin-chats", icon: <BsChatDots size={16} /> },
  { label: "Purchases", to: "/admin-purchases", icon: <BsCreditCard size={16} /> },
];

function Sidebar() {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const check = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (!mobile) setIsOpen(true);
      else setIsOpen(false);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  useEffect(() => {
    if (isMobile) setIsOpen(false);
  }, [pathname, isMobile]);

  const handleLogout = () => {
    localStorage.removeItem("userToken");
    navigate("/login");
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && isMobile && (
        <div
          className="fixed inset-0 z-[998] bg-black/50 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Mobile hamburger button */}
      {!isOpen && isMobile && (
        <button
          onClick={() => setIsOpen(true)}
          className="fixed top-4 left-4 z-[997] md:hidden bg-white shadow-lg rounded-xl p-2.5 border border-gray-100"
        >
          <MdMenu size={20} className="text-gray-700" />
        </button>
      )}

      {/* Sidebar */}
      <div
        className={`fixed md:relative top-0 left-0 h-screen z-[999] bg-gray-900 flex flex-col transition-all duration-300 ease-in-out
          ${isMobile ? (isOpen ? "w-64 translate-x-0" : "w-64 -translate-x-full") : "w-64"}
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/60">
          <div className="flex items-center gap-3">
            <img
              src="/images/logo-transparent.png"
              alt="Perfect Shelters"
              className="h-8 w-8 rounded-lg object-contain"
            />
            <div>
              <p className="text-white font-bold text-sm leading-tight">Perfect Shelters</p>
              <p className="text-[#1877F2] text-[10px] font-semibold uppercase tracking-widest">Admin</p>
            </div>
          </div>
          {isMobile && (
            <button onClick={() => setIsOpen(false)} className="text-gray-400 hover:text-white transition-colors p-1">
              <MdClose size={20} />
            </button>
          )}
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 py-5 space-y-1 overflow-y-auto">
          <p className="text-gray-500 text-[10px] font-semibold uppercase tracking-widest px-3 mb-3">Menu</p>
          {NAV_ITEMS.map(({ label, to, icon }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-[#1877F2]/10 text-[#1877F2] border border-[#1877F2]/20"
                    : "text-gray-400 hover:bg-gray-800 hover:text-white"
                }`
              }
            >
              <span className="flex-shrink-0">{icon}</span>
              <span className="whitespace-nowrap">{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="px-3 pb-5 border-t border-gray-700/60 pt-4">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-gray-400 hover:bg-red-500/10 hover:text-red-400 transition-all duration-200 w-full"
          >
            <BsBoxArrowRight size={16} />
            <span>Log Out</span>
          </button>
        </div>
      </div>
    </>
  );
}

export default Sidebar;
